<template>
  <v-dialog
    v-model="showDialog"
    :max-width="maxWidth"
    :persistent="persistent"
    :class="props.class"
    :attach="props.attach"
    :fullscreen="isMobile || props.fullscreen"
    class="w-modal"
  >
    <div
      class="modal__container"
      :class="{
        'modal__container--edge-to-edge': props.edgeToEdge,
        'modal__container--height-stable': props.heightPreset === 'stable',
        'modal__container--fullscreen': props.fullscreen,
        'modal__container--surface-default': props.surface === 'default'
      }"
      :style="props.containerStyle"
      :data-testid="sectionTag ? sectionTag : undefined"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="modalTitleId"
      @keydown.capture="captureKeydown"
      @keydown.escape="handleEscape"
    >
      <div
        class="modal__header"
        :class="[
          {
            'modal__header--grounded': props.groundedHeader,
            'modal__header--borderless': props.groundedHeader && props.hideHeaderBorder
          },
          props.groundedHeader
            ? `modal__header--grounded-padding-${props.groundedHeaderPadding}`
            : undefined
        ]"
      >
        <div
          v-if="props.groundedHeader"
          :data-testid="sectionTag ? `${sectionTag}-header` : undefined"
        >
          <slot name="header" :title-id="modalTitleId">
            <h2 :id="modalTitleId" :style="headerSlotStyle" :class="headerSlotClass">{{ getHeadLine }}</h2>
          </slot>
        </div>
        <div v-if="!hideCloseButton" class="modal__close-wrapper">
          <WButton
            :section-tag="sectionTag ? `${sectionTag}-close` : undefined"
            variant="icon"
            :icon="mdiClose"
            :label="$t('app.common.buttons.close')"
            class="btn__close"
            @click="close()"
          />
        </div>
      </div>

      <div
        ref="modalContent"
        class="modal__content"
        :class="[
          {
            'modal__content--grounded': props.groundedHeader,
            'modal__content--form': isFormModal,
            'modal__content--has-scroll': hasScroll,
            'modal__content--edge-to-edge': props.edgeToEdge,
            'modal__content--nested-scroll': props.contentScrollMode === 'nested'
          },
          props.groundedHeader
            ? `modal__content--grounded-padding-${props.groundedContentPadding}`
            : undefined
        ]"
      >
        <div
          v-if="props.allowIcon || !props.groundedHeader"
          class="modal__header-group"
        >
          <div
            v-if="props.allowIcon"
            class="modal__icon"
            :data-testid="sectionTag ? `${sectionTag}-icon` : undefined"
          >
            <slot name="icon">
              <div class="modal__icon-circle" :style="iconCircleStyle">
                <v-icon
                  :data-testid="sectionTag ? `${sectionTag}-icon-glyph` : undefined"
                  :icon="getIcon"
                  size="30"
                  :color="iconColor"
                />
              </div>
            </slot>
          </div>
          <div
            v-if="!props.groundedHeader"
            class="modal__title"
            :data-testid="sectionTag ? `${sectionTag}-header` : undefined"
          >
            <slot name="header" :title-id="modalTitleId">
              <h2 :id="modalTitleId" :style="headerSlotStyle" :class="headerSlotClass">{{ getHeadLine }}</h2>
            </slot>
          </div>
        </div>
        <div
          :data-testid="sectionTag ? `${sectionTag}-content` : undefined"
          class="modal__body"
        >
          <template v-if="props.bodyText">
            <slot name="body">
              <p v-dompurify-html="bodyText" class="body-regular-large text-center" :style="{ color: tokens.color.content.text.secondary }"></p>
            </slot>
          </template>
          <template v-else>
            <slot name="content"></slot>
          </template>
        </div>
        <div
          v-if="affectedAreas?.length"
          class="modal__affected-areas"
          :data-testid="sectionTag ? `${sectionTag}-affected-areas` : undefined"
        >
          <div class="modal__affected-areas-header">
            <p class="label-regular-medium">
              {{ affectedAreasTitle ?? $t('app.common.modal.affectedAreas') }}
            </p>
          </div>
          <div class="modal__affected-areas-list">
            <div
              v-for="(area, idx) in affectedAreas"
              :key="idx"
              class="modal__affected-areas-row"
              :data-testid="sectionTag ? `${sectionTag}-affected-areas-row-${idx}` : undefined"
            >
              <p class="label-semibold-large">{{ area.label }}</p>
              <p class="label-regular-large modal__affected-areas-value">{{ area.value }}</p>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="!hideActions"
        class="modal__actions"
        :class="{
          'modal__actions--spread': actionsLayout === 'spread'
        }"
      >
        <slot name="actions">
          <div :class="{ 'modal__action-cancel': actionsLayout === 'spread' }">
            <WButton
              :section-tag="sectionTag ? `${sectionTag}-cancel` : undefined"
              :label="getCancelLabel"
              :variant="actionsLayout === 'spread' ? 'tertiary' : 'utility'"
              :custom-style="cancelStyle"
              @click="cancel()"
            />
          </div>
          <WButton
            :section-tag="sectionTag ? `${sectionTag}-${confirmLabelSlug}` : undefined"
            :label="getConfirmLabel"
            :loading="confirmLoading"
            :custom-style="confirmStyle"
            :disabled="confirmDisabled"
            :href="confirmButtonLink"
            :target="confirmButtonLink ? '_blank' : undefined"
            :variant="props.confirmVariant ?? (props.actionType === Actions.Destructive ? 'destructive' : 'primary')"
            @click="confirm()"
          />
        </slot>
      </div>
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
import { slugify } from '@/composables/useDataTestid'
import { computed, onBeforeUnmount, ref, watch, type StyleValue, type CSSProperties } from 'vue'
import { mdiClose, mdiCheckCircle, mdiPencil, mdiPlusThick, mdiAlertCircle } from '@mdi/js'
import { $t } from '@/plugins/i18n'

import { tokens } from '@/plugins/vuetify'
import WButton from '@wasabi/actions/WButton.vue'
import Actions from '@/composables/Actions'
import { useDisplay } from 'vuetify'

export type WModalHeightPreset = 'content' | 'stable'
export type WModalContentScrollMode = 'modal' | 'nested'
export type WModalGroundedContentPadding = 'medium' | 'large'
export type WModalGroundedHeaderPadding = 'medium' | 'large'
export type WModalSurface = 'default' | 'secondary'

export interface WModalProps {
  sectionTag?: string
  modelValue?: boolean
  maxWidth?: string
  actionType?: string
  headLine?: string
  bodyText?: string
  confirmText?: string
  confirmDisabled?: boolean
  confirmLoading?: boolean
  allowIcon?: boolean
  icon?: string
  groundedHeader?: boolean
  edgeToEdge?: boolean
  hideHeaderBorder?: boolean
  /** Take over the whole viewport. Independent of the automatic mobile takeover (`xs`), which stays
   *  on regardless — this only adds a caller-driven expand, e.g. the Guest Inbox modal's expand
   *  control. */
  fullscreen?: boolean
  hideActions?: boolean
  hideCloseButton?: boolean
  cancelText?: string
  class?: Record<string, unknown>
  attach?: string
  cancelStyle?: CSSProperties
  confirmStyle?: CSSProperties
  confirmVariant?: 'primary' | 'destructive'
  confirmButtonLink?: string
  headerSlotStyle?: StyleValue
  headerSlotClass?: string
  containerStyle?: StyleValue
  heightPreset?: WModalHeightPreset
  contentScrollMode?: WModalContentScrollMode
  groundedContentPadding?: WModalGroundedContentPadding
  groundedHeaderPadding?: WModalGroundedHeaderPadding
  surface?: WModalSurface
  actionsLayout?: 'center' | 'spread'
  persistent?: boolean
  affectedAreas?: { label: string; value: string | number }[]
  affectedAreasTitle?: string
}

const props = withDefaults(defineProps<WModalProps>(), {
  sectionTag: 'unspecified',
  modelValue: undefined,
  maxWidth: '700',
  actionType: Actions.CancelConfirm,
  headLine: '',
  bodyText: '',
  confirmText: '',
  confirmDisabled: false,
  confirmLoading: false,
  allowIcon: true,
  icon: '',
  groundedHeader: false,
  edgeToEdge: false,
  hideHeaderBorder: false,
  fullscreen: false,
  hideActions: false,
  hideCloseButton: false,
  cancelText: '',
  class: () => ({}),
  // ADX override, see scripts/sync-wasabi/config.json (upstream: 'body').
  attach: 'vue-component-wrapper',
  cancelStyle: () => ({}),
  confirmVariant: undefined,
  confirmStyle: () => ({}),
  confirmButtonLink: undefined,
  headerSlotStyle: () => ({}),
  headerSlotClass: '',
  containerStyle: () => ({}),
  heightPreset: 'content',
  contentScrollMode: 'modal',
  groundedContentPadding: 'large',
  groundedHeaderPadding: 'large',
  surface: 'secondary',
  actionsLayout: 'center',
  persistent: false,
  affectedAreas: undefined,
  affectedAreasTitle: undefined
})

const modalTitleId = computed(() => `w-modal-title-${props.sectionTag}`)

const { xs } = useDisplay()
const isMobile = computed(() => xs.value)

const isFormModal = computed(() =>
  props.actionType === Actions.CreateForm || props.actionType === Actions.EditForm
)

const emit = defineEmits(['update:modelValue', 'confirm', 'close', 'cancel'])

const modalContent = ref<HTMLDivElement | null>(null)

const hasScroll = ref<boolean>(false)
let resizeObserver: ResizeObserver | null = null

const SCROLL_THRESHOLD = 10

const updateScrollState = () => {
  const el = modalContent.value
  if (!el) return
  const canScroll = el.scrollHeight > el.clientHeight + SCROLL_THRESHOLD
  hasScroll.value = canScroll
}

const cleanupScrollListeners = () => {
  if (resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  }
  modalContent.value?.removeEventListener('scroll', updateScrollState)
}

watch(modalContent, (el) => {
  cleanupScrollListeners()
  if (el) {
    el.addEventListener('scroll', updateScrollState, { passive: true })
    resizeObserver = new ResizeObserver(updateScrollState)
    resizeObserver.observe(el)
    updateScrollState()
  } else {
    hasScroll.value = false
  }
})

onBeforeUnmount(cleanupScrollListeners)

const showDialog = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
})

const getIcon = computed(() => {
  if (props.icon) {
    return props.icon
  }
  switch (props.actionType) {
    case Actions.CreateForm:
      return mdiPlusThick
    case Actions.EditForm:
      return mdiPencil
    case Actions.Destructive:
      return mdiAlertCircle
    case Actions.CancelConfirm:
    default:
      return mdiCheckCircle
  }
})

const iconColor = computed(() => {
  switch (props.actionType) {
    case Actions.CreateForm:
    case Actions.EditForm:
      return tokens.color.status.info.icon
    case Actions.Destructive:
      return tokens.color.status.danger.icon
    case Actions.CancelConfirm:
    default:
      return tokens.color.status.warning.icon
  }
})

const iconCircleStyle = computed(() => {
  let bg: string = tokens.color.status.warning.surface
  if (props.actionType === Actions.CreateForm || props.actionType === Actions.EditForm) {
    bg = tokens.color.status.info.surface
  } else if (props.actionType === Actions.Destructive) {
    bg = tokens.color.status.danger.surface
  }
  return {
    backgroundColor: bg,
    width: '48px',
    height: '48px',
    borderRadius: tokens.borderRadius.layer.extraLarge,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: '0',
  }
})

const getConfirmLabel = computed(() => {
  if (props.confirmText) {
    return props.confirmText
  }
  switch (props.actionType) {
    case Actions.CancelConfirm:
      return $t('app.common.buttons.confirm')
    case Actions.CreateForm:
      return $t('app.common.buttons.save')
    case Actions.EditForm:
      return $t('app.common.buttons.update')
    default:
      return $t('app.common.buttons.ok')
  }
})

// Falls back to `confirm` so a label with nothing slug-able cannot yield a bare trailing hyphen.
const confirmLabelSlug = computed(() => slugify(getConfirmLabel.value) || 'confirm')

const getCancelLabel = computed(() => {
  if (props.cancelText) {
    return props.cancelText
  }
  switch (props.actionType) {
    case Actions.CancelConfirm:
    case Actions.CreateForm:
    case Actions.EditForm:
      return $t('app.common.buttons.cancel')
    default:
      return $t('app.common.buttons.close')
  }
})

const getHeadLine = computed(() => {
  if (props.headLine) {
    return props.headLine
  }
  switch (props.actionType) {
    case Actions.CreateForm:
      return $t('app.common.dialogs.form.create.headline')
    case Actions.EditForm:
      return $t('app.common.dialogs.form.edit.headline')
    case Actions.CancelConfirm:
    default:
      return $t('app.common.dialogs.cancelConfirm.headline')
  }
})

const close = (): void => {
  emit('close')
  showDialog.value = false
}

// Per-instance flag: in <script setup> top-level bindings compile into setup(),
// so each mounted WModal gets its own closure — do NOT hoist this to module scope.
let escapeConsumedByDropdown = false

// Vuetify overlay menus that consume Escape themselves. WDropdown/WAutoSuggest wrap
// v-autocomplete; v-select/v-combobox are covered too in case a future modal uses one.
const OPEN_MENU_SELECTOR =
  '.v-autocomplete--active-menu, .v-select--active-menu, .v-combobox--active-menu'

const captureKeydown = (event: KeyboardEvent) => {
  if (event.key !== 'Escape') return
  const container = event.currentTarget as HTMLElement
  escapeConsumedByDropdown = !!container.querySelector(OPEN_MENU_SELECTOR)
}

const handleEscape = () => {
  const wasConsumedByDropdown = escapeConsumedByDropdown
  escapeConsumedByDropdown = false

  if (wasConsumedByDropdown || props.persistent) return
  close()
}

const cancel = (): void => {
  emit('cancel')
  showDialog.value = false
}

const confirm = (): void => {
  emit('confirm')
}

const scrollToTop = (): void => {
  if (modalContent.value) {
    modalContent.value.scrollTop = 0
  }
}

defineExpose({ scrollToTop })
</script>

<!-- UNSCOPED: v-dialog teleports content outside the component tree.
     Scoped styles and v-bind() do not reach teleported content.
     Global CSS custom properties from design-tokens.scss (on :root) are used directly via var(). -->
<style lang="scss">
.w-modal .modal__container {
  border-radius: var(--border-radius-layer-extra-large) !important;
  background-color: var(--color-background-surface-secondary) !important;
  box-shadow: 0px 10px 24px 0px rgba(0, 0, 0, 0.18);
  padding: var(--spacing-gap-large) var(--spacing-gap-large) var(--spacing-gap-extra-large);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

.w-modal .modal__container--edge-to-edge {
  background-color: var(--color-background-surface-default) !important;
  padding: 0;
}

.w-modal .modal__container--surface-default {
  background-color: var(--color-background-surface-default) !important;
}

.w-modal .modal__container--height-stable {
  height: min(800px, calc(100dvh - var(--spacing-gap-extra-large)));
}

.w-modal .modal__container--height-stable .modal__body {
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  min-height: 0;
}

.w-modal .modal__container--height-stable .modal__body > .w-loading-region {
  flex: 1 1 0;
  min-height: 0;
}

.w-modal
  .modal__container--height-stable
  .modal__body
  > .w-loading-region
  > .w-loading-region__content-grid,
.w-modal
  .modal__container--height-stable
  .modal__body
  > .w-loading-region
  > .w-loading-region__content-grid
  > .w-loading-region__layer {
  height: 100%;
  min-height: 0;
}

/* Declared after --height-stable so it overrides that rule's `min(800px, ...)` cap, and with
   !important on the radius because the base .modal__container radius is itself !important.
   Deliberately keyed on the `fullscreen` prop alone, not on `isMobile || fullscreen`: the mobile
   takeover predates this and every existing WModal consumer relies on how it renders today. */
.w-modal .modal__container--fullscreen {
  height: 100%;
  max-height: 100%;
  border-radius: 0 !important;
}

.w-modal .modal__header {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
  z-index: 2;
}

.w-modal .modal__header--grounded {
  justify-content: space-between;
  border-bottom: 1px solid var(--color-border-default);
}

.w-modal .modal__header--grounded-padding-medium {
  padding-bottom: var(--spacing-gap-medium);
}

.w-modal .modal__header--grounded-padding-large {
  padding-bottom: var(--spacing-gap-large);
}

.w-modal .modal__header--grounded > div:first-child {
  flex: 1;
  min-width: 0;
}

.w-modal .modal__close-wrapper {
  margin-left: auto;
}

.w-modal .modal__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-gap-medium);
  overflow-x: hidden;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  z-index: 1;
  padding-inline: 4px;
}

.w-modal .modal__content--form {
  gap: var(--spacing-gap-extra-large);
}

.w-modal .modal__content--grounded {
  align-items: stretch;
}

.w-modal .modal__content--grounded-padding-medium {
  padding-top: var(--spacing-gap-medium);
}

.w-modal .modal__content--grounded-padding-large {
  padding-top: var(--spacing-gap-large);
}

.w-modal .modal__content--nested-scroll {
  overflow-y: hidden;
}

.w-modal .modal__content--edge-to-edge {
  align-items: stretch;
  gap: 0;
  padding: 0;
  overflow: hidden;
}

.w-modal .modal__content--edge-to-edge > .modal__body {
  display: flex;
  flex: 1;
  min-height: 0;
}

.w-modal .modal__header--borderless {
  border-bottom: none !important;
  padding-bottom: 0 !important;
}

.w-modal .modal__header-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-gap-medium);
}

.w-modal .modal__icon {
  text-align: center;
}

.w-modal .modal__title {
  text-align: center;
}

.w-modal .modal__body {
  width: 100%;
}

.w-modal .modal__affected-areas {
  width: 100%;
  max-width: 429px;
  max-height: 185px;
  border-radius: var(--border-radius-layer-medium);
  background-color: var(--color-background-surface-muted);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.w-modal .modal__affected-areas-header {
  flex-shrink: 0;
  padding: 16px 16px 8px;
  color: var(--color-content-text-secondary);
  border-bottom: 1px solid var(--color-border-default);
}

.w-modal .modal__affected-areas-list {
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0;
}

.w-modal .modal__affected-areas-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 16px;
  color: var(--color-content-text-primary);
}

.w-modal .modal__affected-areas-value {
  white-space: nowrap;
  flex-shrink: 0;
}

.w-modal .modal__actions {
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: var(--spacing-gap-extra-large);
  gap: var(--spacing-gap-small);
  flex-shrink: 0;
  z-index: 2;
}

.w-modal .modal__actions--spread {
  justify-content: flex-start;
  gap: var(--spacing-gap-large);
}

.w-modal .modal__actions--spread .modal__action-cancel {
  flex: 1 0 0;
}

.w-modal .btn__close {
  width: 28px !important;
  height: 28px !important;
  min-width: 28px !important;
  min-height: 28px !important;
  padding: 0 !important;
}

.w-modal .btn__close .v-icon {
  font-size: 14px !important;
}

.w-modal h2 {
  margin: 0 !important;
  padding: 0 !important;
}

/* Teleported modal content sits outside the component tree. Redeclare typography
   classes here so the tag() mixin resolves via :root CSS custom properties. */
.w-modal {
  h1, h2 { @include tag(heading-bold-large); }
  h3 { @include tag(heading-bold-medium); }
  h4 { @include tag(heading-bold-small); }

  @each $name, $_ in $typography {
    .#{$name} { @include tag($name); }
  }

  @each $name, $_ in $typography-legacy {
    .#{$name} { @include tag($name); }
  }
}

.w-modal .modal__content {
  scrollbar-width: none;
}

.w-modal .modal__content--has-scroll {
  scrollbar-width: thin;
  scrollbar-color: var(--color-action-primary-disabled-text) transparent;
}

@media (max-width: 600px) {
  .w-modal {
    margin-top: 50px;
  }

  .w-modal .v-overlay__content > .modal__container {
    border-radius: 0 !important;
  }

  .w-modal .modal__container--edge-to-edge {
    height: 100%;
  }

  .w-modal .modal__container--height-stable {
    height: 100%;
  }
}
</style>
